export default class CanvasText {
  public brokenLines: string[] = [];

  private isChanged = false;

  private actualText = '';

  public width = 0;

  get text() {
    return this.actualText;
  }

  set text(newText: string) {
    if (this.actualText !== newText) {
      this.isChanged = true;
      this.actualText = newText;
    }
  }

  private static ellipsisTextAndReturn(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
  ) {
    const ellipsis = '...';
    const ellipsisWidth = ctx.measureText(ellipsis).width;
    let { width } = ctx.measureText(text);
    if (width <= maxWidth) {
      return {
        width,
        line: text
      };
    }
    let str = '';
    let len = text.length - 1;
    width = Number.MAX_VALUE;
    while (width >= maxWidth - ellipsisWidth && len > 0) {
      str = text.substring(0, len);
      width = ctx.measureText(str).width;
      len -= 1;
    }
    return {
      line: str + ellipsis,
      width: width + ellipsisWidth
    };
  }

  /**
   * @method ellipsisText
   * @description Wrap text in single lines with 3 dots which can fit
   * @param ctx canvas 2d context
   * @param maxWidth
   */
  ellipsisText(
    ctx: CanvasRenderingContext2D,
    maxWidth: number,
  ) {
    if (this.isChanged) {
      this.width = 0;
      const res = CanvasText.ellipsisTextAndReturn(ctx, this.actualText, maxWidth);
      this.brokenLines = [res.line];
      this.width = res.width;
      this.isChanged = false;
    }
  }

  /**
   * @method wrapText
   * @description Wrap text in multiple lines which can fit
   * @param ctx canvas 2d context
   * @param maxWidth
   * @param forced to recalculate even if text haven't changed
   */
  wrapText(
    ctx: CanvasRenderingContext2D,
    maxWidth: number,
    maxLines = 0,
    forced = false,
    suffix = '',
    minWidth = 50,
  ) {
    if (!this.isChanged && !forced) {
      return;
    }
    const words = this.actualText.split(' ');
    this.width = 0;

    if (maxWidth <= 0) {
      // no limit
      this.brokenLines = [`${this.actualText} ${suffix}`];
      this.width = ctx.measureText(this.brokenLines[0]).width;
      return;
    }

    maxWidth = Math.max(maxWidth, minWidth);

    let lines: string[] = [];
    let currentLine = '';
    const allDone = words.every((word) => {
      const result = this.wordInCurrentLine(lines, currentLine, word, maxWidth, ctx);
      lines = result.lines;
      currentLine = result.currentLine;
      if (maxLines > 0 && result.lines.length >= maxLines) {
        return false;
      }
      return true;
    });
    lines.push(currentLine);

    if (!allDone) {
      let lastLine = `${lines[maxLines - 1]}...`;
      if (ctx.measureText(lastLine).width > maxWidth) {
        lastLine = `${lastLine.substring(0, lastLine.length - 6)}...`;
      }
      lines[maxLines - 1] = lastLine;
      this.brokenLines = lines.slice(0, maxLines);
    } else {
      this.brokenLines = lines;
    }
    this.isChanged = false;

    if (suffix.length === 0) {
      return;
    }
    // append the suffix in the last line
    const lineCount = this.brokenLines.length;
    // fit the suffix with lastLine
    const suffixLen = ctx.measureText(suffix).width;
    if (suffixLen >= maxWidth) {
      this.brokenLines[lineCount - 1] = suffix;
      this.brokenLines[lineCount - 2] = `${this.brokenLines[lineCount - 2].substring(0, this.brokenLines[lineCount - 2].length - 6)}...`;
      return;
    }
    const res = CanvasText.ellipsisTextAndReturn(ctx, this.brokenLines[lineCount - 1], maxWidth - suffixLen);
    this.brokenLines[lineCount - 1] = `${res.line} ${suffix}`;
    const { width } = ctx.measureText(this.brokenLines[lineCount - 1]);
    this.width = Math.max(this.width, width);
  }

  /**
   * @method wordInCurrentLine
   * @description Word in current line present on each connector
   * @param lines
   * @param currentLine
   * @param word
   * @param maxWidth
   * @param ctx canvas context
   * @returns array of lines and current line
   */
  wordInCurrentLine(lines: string[], currentLine: string, word: string, maxWidth: number, ctx: CanvasRenderingContext2D) {
    // can the word be fit
    if (word === '\n') {
      lines.push(currentLine);
      currentLine = '';
    } else {
      if (ctx.measureText(word).width > maxWidth) {
        const wordParts = this.breakWord(ctx, word, maxWidth);
        word = wordParts.pop() ?? '';
        if (currentLine.length > 0) {
          lines.push(currentLine);
        }
        lines.push(...wordParts);
        currentLine = '';
      }
      const line = currentLine.length === 0 ? word : `${currentLine} ${word}`;
      const { width } = ctx.measureText(line);
      if (width > maxWidth) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = line;
        this.width = Math.max(this.width, width);
      }
    }
    return { lines, currentLine };
  }

  /**
   * @method breakWord
   * @description Break a single word to fit
   * @param ctx canvas 2d context
   * @param text
   * @param maxWidth
   * @returns array of lines
   */
  private breakWord(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
  ): string[] {
    const lines: string[] = [];

    const len = text.length;
    let currentLine = '';

    for (let i = 0; i < len; i += 1) {
      const line = currentLine + text[i];
      const { width } = ctx.measureText(`${line}-`);
      if (width > maxWidth) {
        lines.push(`${currentLine}-`);
        currentLine = text[i];
        this.width = Math.max(this.width, width);
      } else {
        currentLine = line;
      }
    }
    lines.push(currentLine);

    return lines;
  }
}
